import { Link } from 'react-router';
import { Users, Car, CreditCard, TrendingUp, ArrowUpRight, AlertTriangle, CheckCircle, Clock, Ban } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const MONTHLY = [
  { month: 'Jan', users: 850, listings: 420, revenue: 42000 },
  { month: 'Feb', users: 1200, listings: 580, revenue: 58000 },
  { month: 'Mar', users: 980, listings: 490, revenue: 52000 },
  { month: 'Apr', users: 1650, listings: 820, revenue: 88000 },
  { month: 'May', users: 1420, listings: 710, revenue: 72000 },
  { month: 'Jun', users: 2100, listings: 1050, revenue: 115000 },
];

const RECENT_USERS = [
  { name: 'Mohammed A.', type: 'Buyer', joined: '2 min ago', status: 'active' },
  { name: 'Karimi Motors', type: 'Dealer', joined: '15 min ago', status: 'pending' },
  { name: 'Fatima Z.', type: 'Seller', joined: '1 hour ago', status: 'active' },
  { name: 'Atlas Cars', type: 'Dealer', joined: '3 hours ago', status: 'pending' },
];

const RECENT_LISTINGS = [
  { title: 'Mercedes GLE 2022', seller: 'Karimi Motors', status: 'active', price: 720000 },
  { title: 'BMW X5 2023', seller: 'Atlas Cars', status: 'pending', price: 850000 },
  { title: 'Toyota Corolla 2021', seller: 'Mohammed A.', status: 'flagged', price: 185000 },
];

export function AdminDashboard() {
  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl text-[#2D323A]">Admin Dashboard</h1>
        <p className="text-[#717182]">Platform overview — June 2024</p>
      </div>

      {/* Alert */}
      <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-xl p-4 flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
          <span className="text-sm text-[#2D323A]">3 listings flagged for review · 5 dealer applications pending</span>
        </div>
        <Link to="/admin/vehicles" className="text-sm text-[#1E6DFF] hover:underline">Review →</Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Users', value: '124,500', change: '+2,100 this month', icon: Users, color: 'bg-[#1E6DFF]/10 text-[#1E6DFF]' },
          { label: 'Active Listings', value: '52,300', change: '+1,050 this month', icon: Car, color: 'bg-[#22C55E]/10 text-[#22C55E]' },
          { label: 'Monthly Revenue', value: '115K MAD', change: '+18% vs last month', icon: TrendingUp, color: 'bg-[#F59E0B]/10 text-[#F59E0B]' },
          { label: 'Subscriptions', value: '3,840', change: '+320 this month', icon: CreditCard, color: 'bg-[#1E6DFF]/10 text-[#1E6DFF]' },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${s.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-2xl text-[#2D323A]">{s.value}</p>
              <p className="text-sm text-[#717182]">{s.label}</p>
              <p className="text-xs text-[#22C55E] mt-1 flex items-center gap-0.5">
                <ArrowUpRight className="w-3 h-3" /> {s.change}
              </p>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
          <h3 className="text-[#2D323A] mb-4">New Users & Listings (Monthly)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={MONTHLY}>
              <defs>
                <linearGradient id="usersGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1E6DFF" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="#1E6DFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB' }} />
              <Area type="monotone" dataKey="users" stroke="#1E6DFF" strokeWidth={2} fill="url(#usersGrad)" name="Users" />
              <Area type="monotone" dataKey="listings" stroke="#22C55E" strokeWidth={2} fill="none" name="Listings" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
          <h3 className="text-[#2D323A] mb-4">Monthly Revenue (MAD)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={MONTHLY}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB' }} />
              <Bar dataKey="revenue" fill="#1E6DFF" radius={[6, 6, 0, 0]} name="Revenue (MAD)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-[#E5E7EB]">
          <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
            <h3 className="text-[#2D323A]">Recent Users</h3>
            <Link to="/admin/users" className="text-sm text-[#1E6DFF] hover:underline">View all</Link>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {RECENT_USERS.map((u, i) => (
              <div key={i} className="flex items-center gap-3 p-4">
                <div className="w-9 h-9 bg-[#F5F7FA] rounded-xl flex items-center justify-center text-[#717182]">
                  {u.name[0]}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-[#2D323A]">{u.name}</p>
                  <p className="text-xs text-[#717182]">{u.type} · {u.joined}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-lg ${u.status === 'active' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#F59E0B]/10 text-[#F59E0B]'}`}>
                  {u.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E7EB]">
          <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
            <h3 className="text-[#2D323A]">Recent Listings</h3>
            <Link to="/admin/vehicles" className="text-sm text-[#1E6DFF] hover:underline">View all</Link>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {RECENT_LISTINGS.map((l, i) => (
              <div key={i} className="flex items-center gap-3 p-4">
                <div className="flex-1">
                  <p className="text-sm text-[#2D323A]">{l.title}</p>
                  <p className="text-xs text-[#717182]">{l.seller} · {l.price.toLocaleString()} MAD</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-lg flex items-center gap-1 ${
                  l.status === 'active' ? 'bg-[#22C55E]/10 text-[#22C55E]' :
                  l.status === 'pending' ? 'bg-[#F59E0B]/10 text-[#F59E0B]' :
                  'bg-[#EF4444]/10 text-[#EF4444]'
                }`}>
                  {l.status === 'active' ? <CheckCircle className="w-3 h-3" /> : l.status === 'flagged' ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                  {l.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
